/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { ModalController } from "../../../model/navigation";
import { InputAction } from "../../../model/enums/InputAction";
import {
  dismissUnityDialogWithoutAction,
  focusNextAction,
  focusPrevAction,
  selectUnityDialogActions,
  selectUnityDialogFocusedActionIndex,
  selectUnityDialogOnBackActionId,
  triggerUnityDialogAction
} from "../../../viewModel/unityDialog";
import type { UnityAppDispatch, UnityRootState } from "../../../viewModel";

/** Modal name constant for the UnityDialog modal. */
export const UNITY_DIALOG_MODAL_NAME: string = "UNITY_DIALOG";

/**
 * ModalController for the UnityDialog.
 *
 * D-pad routing:
 *   NAV_LEFT/NAV_RIGHT → move focus between actions in the horizontal row.
 *   NAV_UP/NAV_DOWN    → consumed as no-ops (no vertical layout currently
 *                         exists) so input never leaks through to the
 *                         screen behind the dialog.
 *   NAV_OK → activate the focused action.
 *   BACK   → dismiss the dialog outright — no action is activated and no
 *            button's `onPress` callback runs, whichever one has focus. If
 *            the dialog was opened with an `onBackPress` override, that
 *            callback runs instead (e.g. the playback-error dialog uses this
 *            to exit the player on BACK the same way its OK button does,
 *            rather than just cancelling and stranding the user on a player
 *            that isn't playing anything). Otherwise BACK is a pure cancel.
 *
 * All handlers return true to consume the input and prevent it from
 * bubbling to the screen behind the dialog. Every action also swallows its
 * own `keyup` event: some screens (e.g. FullScreenPlayback) register a
 * separate keyup handler for NAV_LEFT/NAV_RIGHT (skip/hold-release), which
 * would otherwise fire once the keydown is consumed here but the keyup falls
 * through the modal stack. The remaining actions swallow keyup defensively —
 * no screen currently reacts to their keyup, but this prevents the same leak
 * if one ever does.
 */
export class UnityDialogController extends ModalController {
  private readonly mDispatch: UnityAppDispatch;
  private readonly mGetState: () => UnityRootState;

  public constructor(
    dispatch: UnityAppDispatch,
    getState: () => UnityRootState
  ) {
    super(UNITY_DIALOG_MODAL_NAME);
    this.mDispatch = dispatch;
    this.mGetState = getState;
  }

  protected initialize(): void {
    this.registerHandler(InputAction.NAV_LEFT, (): boolean => {
      this.mDispatch(focusPrevAction());
      return true;
    });

    this.registerHandler(InputAction.NAV_RIGHT, (): boolean => {
      this.mDispatch(focusNextAction());
      return true;
    });

    // Swallow the matching keyup too — screens with their own NAV_LEFT/RIGHT
    // keyup handler (e.g. skip/hold-release) would otherwise receive it once
    // this modal's keydown handler above has already consumed the keydown.
    this.registerHandler(InputAction.NAV_LEFT, (): boolean => true, "keyup");
    this.registerHandler(InputAction.NAV_RIGHT, (): boolean => true, "keyup");

    // No vertical layout currently exists — consume UP/DOWN as no-ops so
    // they don't leak through to the screen behind the dialog.
    this.registerHandler(InputAction.NAV_UP, (): boolean => true);
    this.registerHandler(InputAction.NAV_DOWN, (): boolean => true);

    this.registerHandler(InputAction.NAV_OK, (): boolean => {
      const state = this.mGetState();
      const actions = selectUnityDialogActions(state);
      const focusedIndex = selectUnityDialogFocusedActionIndex(state);
      const action = actions[focusedIndex];
      if (action !== undefined) {
        triggerUnityDialogAction(this.mDispatch, action.id);
      }
      return true;
    });

    // BACK is a pure cancel by default — never activates the focused action
    // or any other action, and never runs its onPress callback. A dialog
    // opened with an `onBackPress` override gets that callback invoked
    // instead (dismiss → clear registry → invoke, same sequence NAV_OK uses).
    this.registerHandler(InputAction.BACK, (): boolean => {
      const onBackActionId: string = selectUnityDialogOnBackActionId(
        this.mGetState()
      );
      if (onBackActionId !== "") {
        triggerUnityDialogAction(this.mDispatch, onBackActionId);
      } else {
        dismissUnityDialogWithoutAction(this.mDispatch);
      }
      return true;
    });

    // Defensive keyup swallows: no screen currently registers its own keyup
    // handler for these actions, so this is a no-op today, but it future-proofs
    // against any screen behind the dialog later adding one (same leak class
    // as NAV_LEFT/NAV_RIGHT above).
    this.registerHandler(InputAction.NAV_UP, (): boolean => true, "keyup");
    this.registerHandler(InputAction.NAV_DOWN, (): boolean => true, "keyup");
    this.registerHandler(InputAction.NAV_OK, (): boolean => true, "keyup");
    this.registerHandler(InputAction.BACK, (): boolean => true, "keyup");
  }
}
